import { OrganizationContractDocumentSettings } from 'types';

import { ContractTemplatePayload } from '../components/AdminContracts/contractTemplateSchema';
import { DataFilterDisplay } from '../components/filters/FilterButton';

import { api } from './api';

interface TemplateQueryParams {
  pageNum?: number;
  advancedFilters?: string;
  searchTerm?: string;
}

interface DocuSignTemplateInfo {
  name: string;
}

interface UpdateTemplatePayload {
  organizationSettingsId: string;
  contractDocumentSettingsId: string;
  templateData: ContractTemplatePayload;
}

export interface ContractTemplateHistoryDiff {
  path: string;
  from?: unknown;
  to?: unknown;
}

export interface ContractTemplateHistoryEntry {
  timestamp: string;
  actorEmail?: string;
  diffs: ContractTemplateHistoryDiff[];
}

export interface ContractTemplateHistoryResponse {
  data: ContractTemplateHistoryEntry[];
  total: number;
}

interface GetContractTemplateHistoryParams {
  contractDocumentSettingsId: string;
  limit?: number;
  offset?: number;
}

const buildQueryString = (params: TemplateQueryParams | undefined): string => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  if (params.pageNum !== undefined) searchParams.set('pageNum', String(params.pageNum));
  if (params.advancedFilters) searchParams.set('advancedFilters', params.advancedFilters);
  if (params.searchTerm) searchParams.set('searchTerm', params.searchTerm);
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const contractTemplates = api.injectEndpoints({
  endpoints: (build) => ({
    getTemplates: build.query<OrganizationContractDocumentSettings[], TemplateQueryParams>({
      query: (queryParams) => ({
        url: `/admin/contracts/templates${buildQueryString(queryParams)}`,
      }),
      providesTags: () => [{ type: 'ContractTemplates', id: 'LIST' }],
    }),
    getTemplateInfo: build.query<DocuSignTemplateInfo | null, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `/admin/contracts/template/${templateId}`,
      }),
      providesTags: (_result, _error, { templateId }) => [{ type: 'ContractTemplateInfo', id: templateId }],
    }),
    getTemplatesListFilters: build.query<DataFilterDisplay[], void>({
      query: () => ({
        url: `/admin/contracts/templates/filters`,
      }),
    }),
    createTemplate: build.mutation<void, ContractTemplatePayload>({
      query: (templateData) => ({
        url: `/admin/contracts/templates`,
        method: 'POST',
        body: templateData,
      }),
      invalidatesTags: () => [{ type: 'ContractTemplates', id: 'LIST' }],
    }),
    updateTemplate: build.mutation<void, UpdateTemplatePayload>({
      query: ({ organizationSettingsId, contractDocumentSettingsId, templateData }) => ({
        url: `/admin/contracts/templates/${organizationSettingsId}/${contractDocumentSettingsId}`,
        method: 'PATCH',
        body: templateData,
      }),
      invalidatesTags: (_result, _error, { contractDocumentSettingsId }) => [
        { type: 'ContractTemplates', id: 'LIST' },
        {
          type: 'ContractTemplates',
          id: `HISTORY-${contractDocumentSettingsId}`,
        },
      ],
    }),
    getContractTemplateHistory: build.query<ContractTemplateHistoryResponse, GetContractTemplateHistoryParams>({
      query: ({ contractDocumentSettingsId, limit, offset }) => {
        const searchParams = new URLSearchParams();
        if (limit !== undefined) searchParams.set('limit', String(limit));
        if (offset !== undefined) searchParams.set('offset', String(offset));
        const qs = searchParams.toString();
        return {
          url: `/admin/contracts/templates/${contractDocumentSettingsId}/history${qs ? `?${qs}` : ''}`,
        };
      },
      providesTags: (_result, _error, { contractDocumentSettingsId }) => [
        {
          type: 'ContractTemplates',
          id: `HISTORY-${contractDocumentSettingsId}`,
        },
      ],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplatesListFiltersQuery,
  useLazyGetTemplateInfoQuery,
  useLazyGetTemplatesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useGetContractTemplateHistoryQuery,
  useLazyGetContractTemplateHistoryQuery,
} = contractTemplates;
